import React, { useState, type CSSProperties } from 'react';
import type {
  AuthorProfile,
  AuthorReadyManuscript,
  AuthorPublicationPreference,
} from '../types';
import { authorApi } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import {
  CloseIcon,
} from './AuthorIcons';

interface Props {
  initialProfile: AuthorProfile;
  onClose: () => void;
  onProfileUpdated: (updated: AuthorProfile) => void;
}

export const SurveysAndFormsOverlay: React.FC<Props> = ({
  initialProfile,
  onClose,
  onProfileUpdated,
}) => {
  const { notify } = useToast();
  const [activeSection, setActiveSection] = useState<'QUESTIONNAIRE' | 'PREFERENCES' | 'MANUSCRIPTS' | 'FEEDBACK'>('QUESTIONNAIRE');
  const [profile, setProfile] = useState<AuthorProfile>(initialProfile);
  const [pref, setPref] = useState<AuthorPublicationPreference>(
    initialProfile.publication_preferences || {
      target_database: 'Scopus',
      target_quartile: 'Q1-Q2',
      max_coauthors_count: 4,
      open_access_preference: true,
      notes: '',
    }
  );

  const [manuscripts, setManuscripts] = useState<AuthorReadyManuscript[]>([]);
  const [loadingManuscripts, setLoadingManuscripts] = useState(false);

  const [feedbackCoordinator, setFeedbackCoordinator] = useState('Алия Сейткалиева (Старший координатор публикаций)');
  const [feedbackTopic, setFeedbackTopic] = useState('Консультация по распределению долей соавторов');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const loadManuscripts = async () => {
    try {
      setLoadingManuscripts(true);
      const data = await authorApi.getReadyManuscripts();
      setManuscripts(data);
    } catch {
    } finally {
      setLoadingManuscripts(false);
    }
  };

  const handleSaveQuestionnaire = async () => {
    try {
      setSavingProfile(true);
      const updated = await authorApi.updateProfile({
        ...profile,
        publication_preferences: pref,
      });
      setProfile(updated);
      onProfileUpdated(updated);
      notify('Анкета автора успешно обновлена!', 'success');
    } catch (err: any) {
      notify(err?.message || 'Ошибка сохранения анкеты', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleManuscriptInterest = async (id: string) => {
    try {
      const updated = await authorApi.expressManuscriptInterest(id, 'Интерес заявлен через кабинет автора');
      setManuscripts((prev) => prev.map((m) => (m.id === id ? updated : m)));
      notify('Интерес к рукописи зафиксирован! Координатор свяжется с вами.', 'success');
    } catch (err: any) {
      notify(err?.message || 'Ошибка фиксации интереса', 'error');
    }
  };

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) {
      notify('Введите текст сообщения', 'error');
      return;
    }
    try {
      await authorApi.submitFeedback({
        coordinator_name: feedbackCoordinator,
        topic: feedbackTopic,
        rating: feedbackRating,
        message: feedbackMessage,
      });
      setFeedbackSent(true);
      setTimeout(() => {
        setFeedbackSent(false);
        setFeedbackMessage('');
      }, 3000);
    } catch (err: any) {
      notify(err?.message || 'Ошибка отправки', 'error');
    }
  };

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Опросы и формы</small>
              <h2>Анкета, предпочтения и обратная связь</h2>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="cab-tabs">
          {[
            { id: 'QUESTIONNAIRE', label: 'Академ-анкета 3.1–3.24' },
            { id: 'PREFERENCES', label: 'Публикационные предпочтения' },
            { id: 'MANUSCRIPTS', label: 'Готовые статьи в пуле' },
            { id: 'FEEDBACK', label: 'Обратная связь координатору' },
          ].map((sec) => (
            <button
              key={sec.id}
              type="button"
              onClick={() => {
                setActiveSection(sec.id as typeof activeSection);
                if (sec.id === 'MANUSCRIPTS') loadManuscripts();
              }}
              className={`cab-tab${activeSection === sec.id ? ' is-active' : ''}`}
            >
              {sec.label}
            </button>
          ))}
        </div>

        <div className="cab-body">
          {activeSection === 'QUESTIONNAIRE' && (
            <div className="cabinet-stack">
              <article className="cabinet-card" style={{ ['--accent' as string]: '#49ddb7' }}>
                <div className="cabinet-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3>Уровень заполнения профиля</h3>
                    <p>Чем полнее анкета, тем точнее подбор грантов, журналов и соавторов.</p>
                  </div>
                  <strong style={{ color: '#49ddb7', fontSize: 20 }}>
                    {profile.completion_percentage || 75}%
                  </strong>
                </div>
              </article>

              <div className="cabinet-grid3">
                <label className="cabinet-field">
                  <span>3.1 ФИО автора *</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.full_name || ''}
                    onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.2 Текущий статус / роль</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.current_role || ''}
                    onChange={(e) => setProfile({ ...profile, current_role: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.3 Код автора</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.author_code || 'ZA-AU-2026'}
                    disabled
                  />
                </label>

                <label className="cabinet-field">
                  <span>3.4 Место работы / ВУЗ</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.workplace_or_university || ''}
                    onChange={(e) => setProfile({ ...profile, workplace_or_university: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.5 Должность</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.position || ''}
                    onChange={(e) => setProfile({ ...profile, position: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.6 Ученая степень</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.academic_degree || ''}
                    onChange={(e) => setProfile({ ...profile, academic_degree: e.target.value })}
                  />
                </label>

                <label className="cabinet-field">
                  <span>3.7 Ученое звание</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.academic_title || ''}
                    onChange={(e) => setProfile({ ...profile, academic_title: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.8 Основное научное направление</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.primary_field || ''}
                    onChange={(e) => setProfile({ ...profile, primary_field: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.9 Смежные области</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.secondary_fields?.join(', ') || ''}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        secondary_fields: e.target.value.split(',').map((s) => s.trim()),
                      })
                    }
                  />
                </label>

                <label className="cabinet-field">
                  <span>3.10 ORCID iD</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.orcid_id || ''}
                    onChange={(e) => setProfile({ ...profile, orcid_id: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.11 Scopus Author ID</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.scopus_author_id || ''}
                    onChange={(e) => setProfile({ ...profile, scopus_author_id: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.12 Web of Science ResearcherID</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.wos_researcher_id || ''}
                    onChange={(e) => setProfile({ ...profile, wos_researcher_id: e.target.value })}
                  />
                </label>

                <label className="cabinet-field">
                  <span>3.14 Индекс Хирша (Scopus)</span>
                  <input
                    type="number"
                    className="cabinet-input"
                    value={profile.h_index_scopus || 0}
                    onChange={(e) => setProfile({ ...profile, h_index_scopus: Number(e.target.value) })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.15 Индекс Хирша (WoS)</span>
                  <input
                    type="number"
                    className="cabinet-input"
                    value={profile.h_index_wos || 0}
                    onChange={(e) => setProfile({ ...profile, h_index_wos: Number(e.target.value) })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.16 Число публикаций</span>
                  <input
                    type="number"
                    className="cabinet-input"
                    value={profile.total_publications_count || 0}
                    onChange={(e) =>
                      setProfile({ ...profile, total_publications_count: Number(e.target.value) })
                    }
                  />
                </label>

                <label className="cabinet-field">
                  <span>3.18 Контактный Email</span>
                  <input
                    type="email"
                    className="cabinet-input"
                    value={profile.contact_email || ''}
                    onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.19 Контактный телефон</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.contact_phone || ''}
                    onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })}
                  />
                </label>
                <label className="cabinet-field">
                  <span>3.20 Telegram контакт</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={profile.telegram_handle || ''}
                    onChange={(e) => setProfile({ ...profile, telegram_handle: e.target.value })}
                  />
                </label>
              </div>

              <div className="cabinet-actions" style={{ justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="cabinet-btn primary"
                  disabled={savingProfile}
                  onClick={handleSaveQuestionnaire}
                >
                  {savingProfile ? 'Сохранение...' : 'Сохранить анкету'}
                </button>
              </div>
            </div>
          )}

          {activeSection === 'PREFERENCES' && (
            <section className="cabinet-panel" style={{ maxWidth: 640 }}>
              <header>
                <div>
                  <h3>Параметры публикационных предпочтений</h3>
                </div>
              </header>
              <div className="cabinet-panel-body">
                <div className="cabinet-stack">
                  <label className="cabinet-field">
                    <span>Приоритетная наукометрическая база</span>
                    <select
                      className="cabinet-select"
                      value={pref.target_database}
                      onChange={(e) => setPref({ ...pref, target_database: e.target.value })}
                    >
                      <option value="Scopus">Scopus</option>
                      <option value="Web of Science">Web of Science</option>
                      <option value="КОКСНВО МНВО РК">КОКСНВО МНВО РК</option>
                      <option value="РИНЦ">РИНЦ</option>
                    </select>
                  </label>

                  <label className="cabinet-field">
                    <span>Желаемый квартиль издания</span>
                    <select
                      className="cabinet-select"
                      value={pref.target_quartile}
                      onChange={(e) => setPref({ ...pref, target_quartile: e.target.value })}
                    >
                      <option value="Q1">Q1 (Топ 25%)</option>
                      <option value="Q1-Q2">Q1 - Q2</option>
                      <option value="Q3">Q3</option>
                      <option value="Q4">Q4</option>
                    </select>
                  </label>

                  <label className="cabinet-field">
                    <span>Максимальное число соавторов в статье</span>
                    <input
                      type="number"
                      className="cabinet-input"
                      value={pref.max_coauthors_count || 4}
                      onChange={(e) => setPref({ ...pref, max_coauthors_count: Number(e.target.value) })}
                    />
                  </label>

                  <label className="cabinet-field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="pref-oa"
                      checked={pref.open_access_preference}
                      onChange={(e) => setPref({ ...pref, open_access_preference: e.target.checked })}
                    />
                    <span>Предпочитаю публикации открытого доступа</span>
                  </label>

                  <label className="cabinet-field">
                    <span>Дополнительные пожелания</span>
                    <textarea
                      className="cabinet-textarea"
                      value={pref.notes || ''}
                      onChange={(e) => setPref({ ...pref, notes: e.target.value })}
                      rows={3}
                      placeholder="Например: приоритет журналам без APC или с быстрой индексацией"
                    />
                  </label>

                  <div className="cabinet-actions">
                    <button type="button" className="cabinet-btn primary" onClick={handleSaveQuestionnaire}>
                      Сохранить предпочтения
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeSection === 'MANUSCRIPTS' && (
            <div className="cabinet-stack">
              <div className="cabinet-note">
                Готовые статьи из пула академии с открытыми вакансиями для соавторов.
              </div>

              {loadingManuscripts ? (
                <div className="cabinet-note">Загрузка пула статей…</div>
              ) : (
                <div className="cabinet-grid2">
                  {manuscripts.map((m) => (
                    <article key={m.id} className="cabinet-card" style={{ ['--accent' as string]: '#49ddb7' }}>
                      <div className="cabinet-actions" style={{ marginBottom: 8, alignItems: 'center' }}>
                        <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                          {m.code}
                        </span>
                        <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                          {m.target_database} {m.quartile && `(${m.quartile})`}
                        </span>
                      </div>
                      <h3>{m.title}</h3>
                      <p>{m.abstract_snippet}</p>
                      <div className="cabinet-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13 }}>
                          Свободно мест: <strong style={{ color: '#49ddb7' }}>{m.available_coauthor_slots}</strong>
                        </span>
                        <button
                          type="button"
                          className="cabinet-btn primary"
                          onClick={() => handleManuscriptInterest(m.id)}
                        >
                          Забронировать квоту
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'FEEDBACK' && (
            <section className="cabinet-panel" style={{ maxWidth: 560 }}>
              <header>
                <div>
                  <h3>Прямой канал связи с координатором</h3>
                </div>
              </header>
              <div className="cabinet-panel-body">
                <div className="cabinet-stack">
                  <label className="cabinet-field">
                    <span>Координатор</span>
                    <input
                      type="text"
                      className="cabinet-input"
                      value={feedbackCoordinator}
                      onChange={(e) => setFeedbackCoordinator(e.target.value)}
                    />
                  </label>

                  <label className="cabinet-field">
                    <span>Тема обращения</span>
                    <input
                      type="text"
                      className="cabinet-input"
                      value={feedbackTopic}
                      onChange={(e) => setFeedbackTopic(e.target.value)}
                    />
                  </label>

                  <div className="cabinet-field">
                    <span>Оценка взаимодействия</span>
                    <div className="cabinet-actions">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFeedbackRating(star)}
                          className={`cabinet-btn${feedbackRating >= star ? ' primary' : ''}`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="cabinet-field">
                    <span>Текст сообщения</span>
                    <textarea
                      className="cabinet-textarea"
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      rows={4}
                      placeholder="Напишите ваш вопрос, замечание или предложение координатору..."
                    />
                  </label>

                  {feedbackSent ? (
                    <div className="cabinet-note ok">Обратная связь успешно отправлена координатору!</div>
                  ) : null}

                  <div className="cabinet-actions">
                    <button type="button" className="cabinet-btn primary" onClick={handleSendFeedback}>
                      Отправить координатору
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
